import React, { useEffect, useMemo, useState } from "react";
import API from "../../services/api";
import { accessibleTagCount, accessError, resourcesForUser } from "../../utils/accessManagement";

function ResourceChoices({ title, name, rows, selected, onChange, label, disabled }) {
    const [query, setQuery] = useState("");
    const matches = rows.filter((row) => label(row).toLowerCase().includes(query.toLowerCase()));
    return <fieldset className="access-choices" disabled={disabled}>
        <legend>{title} ({selected.length} selected)</legend>
        <input aria-label={`Search ${title.toLowerCase()}`} placeholder="Search" value={query} onChange={(event) => setQuery(event.target.value)} />
        <div className="access-choice-list">
            {matches.map((row) => <label key={row.id}>
                <input type="checkbox" name={name} value={row.id} checked={selected.includes(row.id)} onChange={(event) => onChange(event.target.checked ? [...selected, row.id] : selected.filter((id) => id !== row.id))} />
                {label(row)}
            </label>)}
            {!matches.length && <p>No matching resources.</p>}
        </div>
    </fieldset>;
}

export default function ResourceGrantForm({ user, catalog, busy, onSave }) {
    const [grants, setGrants] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(0);
    const available = useMemo(() => resourcesForUser(catalog, user), [catalog, user]);
    useEffect(() => {
        let active = true;
        setLoading(true);
        setGrants(null);
        setError("");
        API.get(`/access/users/${user.id}/grants/`).then((response) => {
            if (active) setGrants(response.data);
        }).catch((failure) => { if (active) setError(accessError(failure)); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [user.id, reload]);
    const count = grants ? accessibleTagCount(available.tags, available.groups, grants.tag_ids, grants.group_ids) : 0;
    const exceeds = user.max_tags != null && count > user.max_tags;
    const change = (field) => (ids) => setGrants((current) => ({ ...current, [field]: ids }));
    const submit = async (event) => {
        event.preventDefault();
        setError("");
        try { await onSave(grants); } catch (failure) { setError(failure.message); }
    };
    return <section className="access-panel" aria-labelledby="access-grants-heading">
        <h2 id="access-grants-heading">Resource access</h2>
        <p>Group access includes all tags in the group and its child groups, including future additions. Direct and group access to the same tag count once.</p>
        {loading ? <p role="status">Loading current access…</p> : grants ? <form onSubmit={submit}>
            <div className="access-grid">
                <ResourceChoices title="Groups" name="group_ids" rows={available.groups} selected={grants.group_ids} onChange={change("group_ids")} label={(row) => `${row.name} (#${row.id})`} disabled={busy} />
                <ResourceChoices title="Individual tags" name="tag_ids" rows={available.tags} selected={grants.tag_ids} onChange={change("tag_ids")} label={(row) => `${row.name} (#${row.id})`} disabled={busy} />
                <ResourceChoices title="Designer pages" name="page_ids" rows={available.pages} selected={grants.page_ids} onChange={change("page_ids")} label={(row) => row.title} disabled={busy} />
            </div>
            <p role="status">{count} unique tags selected; limit: {user.max_tags ?? "unlimited"}.</p>
            {exceeds && <p className="access-error" role="alert">This selection exceeds the user's limit. Increase the limit or select fewer resources. No partial group access will be saved.</p>}
            <p className="access-hint">Uncheck resources to revoke access. Saving replaces the user's complete resource selection. The server checks the current limits again.</p>
            <button disabled={busy || exceeds} type="submit">{busy ? "Saving…" : "Save resource access"}</button>
        </form> : <button onClick={() => setReload((value) => value + 1)}>Retry loading access</button>}
        {error && <p className="access-error" role="alert">{error}</p>}
    </section>;
}
